import fetch from "../util/api-ajax";
import * as constants from "../../constants";
import * as status from "./pageStatus";
import moment from "moment";
import { HAS_CSA_SERVICE } from "../util/flags";
import { getProperPromoCode } from "../components/CSA/AddCSADialog";

// Get user and CSA information to populate CSAView
export function setSelectedCSA({ fetchedCsa, userID, memberID, allowFailure = false }) {
  return async (dispatch) => {
    if (!HAS_CSA_SERVICE) {
      // Exit early if CSA service is not enabled (used in member initialization to avoid unnecessary API calls)
      return;
    }

    dispatch(status.startProcessing());

    try {
      let csa;
      let user;
      if (fetchedCsa) {
        const csa_data = await fetch(constants.CSA_GET_CSA_URL, {csa_id: fetchedCsa.id});
        csa = csa_data?.csa
        user = await fetch(constants.GET_USER_URL, { userID: csa?.user_id });
      } else {
        try {
          const csa_info = await fetch(constants.CSA_GET_CSA_URL, { user_id: parseInt(userID), member_id: memberID });
          csa = csa_info?.csa;
          // Use the returned user_id just in case userID wasn't provided
          const csa_user_id = csa?.user_id;
          user = await fetch(constants.GET_USER_URL, { userID: csa_user_id });
        } catch (error) {
          // If we don't care about fetch failures, just return empty objects on error
          if (!allowFailure) {
            throw error;
          }
          csa = {};
          user = {};
        }
      }

      dispatch(receiveCSA({ csa }));
      if (user?.billing_address && typeof user.billing_address === "string") {
        user.billing_address = JSON.parse(user.billing_address);
      }
      dispatch(receiveUser({ user }));
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  };
}

function receiveCSA(csa) {
  return {
    type: constants.CSA_RECEIVE_CSA,
    payload: csa,
  };
}

function receiveUser(user) {
  return {
    type: constants.CSA_RECEIVE_USER,
    payload: user,
  };
}

// Create a new parent CSA
export function createCSA(data) {
  return async (dispatch) => {
    dispatch(status.startProcessing());
    try {
      const response = await fetch(constants.CSA_CREATE_CSA_URL, data);
      dispatch(receiveCSA(response));
      dispatch(status.setSuccessMessage("CSA Created"));
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  };
}

export function updateCSA({csa_id, credit_approved_meters}){
  return async (dispatch) => {
    dispatch(status.startProcessing());
    try {
      await fetch(constants.CSA_UPDATE_CSA_URL, {csa_id, credit_approved_meters})

    } catch (error) {
      dispatch(status.setErrorMessage(error.message))
    } finally {
      dispatch(status.finishProcessing())
    }
  }
}

function enrollmentStatus(status) {
  if (['canceled', 'expired', 'fraud'].includes(status)) {
    return 'enrollment failed'
  }
  if (status === 'hold') {
    return 'enrollment held'
  }

  return 'enrollment pending'
}

export function listPendingCSAEnrollments({ csa_id, email, csa_type}) {
  return async (dispatch) => {
    dispatch(receivePendingCSAEnrollments([]));
    if (!csa_id || !email) {
      return;
    }

    dispatch(status.startProcessing());
    try {
      const response = await fetch(constants.ENROLLMENT_SEARCH_URL, { csa_id, email, record_age_in_days: 30 });
      
      const enrollments = response?.list?.filter((e) => !e.member_id).map((enrollment) => (
        {
          meter_type: csa_type,
          member_id: enrollment.member_id,
          meter: {
            meter_identifier: enrollment.meter_identifier,
            status: enrollmentStatus(enrollment.enrollment_status),
            on_csa: "",
          }
        }
      ));
      dispatch(receivePendingCSAEnrollments(enrollments || []));
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  }
}

export function listCSAMembers({ csa_id, page, limit }) {
  return async (dispatch) => {
    // Clear members (sets table loading status)
    dispatch(receiveCSAMembers(null));
    if (!csa_id) {
      return;
    }

    dispatch(status.startProcessing());
    try {
      const response = await fetch(constants.CSA_LIST_MEMBERS_URL, { csa_id, page, limit, fetch_accounts: false, fetch_holds: false });
      const { total, csa_members: csaMembers } = response;
      
      dispatch(receiveCSAMembers(csaMembers || [], Number(total) || 0));
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  };
}

function receiveCSAMembers(members, total = 0) {
  return {
    type: constants.CSA_RECEIVE_MEMBERS,
    payload: { members, total },
  };
}

export function receivePendingCSAEnrollments(enrollments) {
  return {
    type: constants.CSA_RECEIVE_PENDING_ENROLLMENTS,
    payload: { enrollments },
  };
}

// Returns a list of tdsp product offers (not saved to state)
export function fetchProductOffers({ locale, memberID, postal_code, promo_code }) {
  return async (dispatch) => {
    dispatch(status.startProcessing());
    let response;
    try {
      response = await fetch(constants.GET_MEMBER_PRODUCT_CHANGE_OFFERS, { locale, memberID, postal_code, promoCode: promo_code });
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
    return response;
  };
}

export function fetchCurrentOffer({memberID}) {
  return async (dispatch) => {
    dispatch(status.startProcessing())
    let response;
    try {
      response = await fetch(constants.MEMBER_GET_PRODUCT, {memberID})
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
    return response;
  }
}

// Returns meter information for an ESI ID (not saved to state)
export function findMeter({esi_id, tdsp_duns}) {
  return async (dispatch) => {
    dispatch(status.startProcessing());
    let meter;
    try {
      const response = await fetch(constants.METER_SEARCH, { esi_id });
      if (response?.meters?.length) {
        meter = response.meters[0];
        if (meter.duns !== tdsp_duns) {
          dispatch(status.setErrorMessage("Meter is out of selected CSA's TDSP"))
          return;
        }
      } else {
        dispatch(status.setErrorMessage("Meter not found for ESI ID"));
      }
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
    return meter;
  };
}

export function submitEnrollment(props) {
  return async (dispatch) => {
    dispatch(status.startProcessing());
    try {
      const userInfo = props.userInfo;
      const csaInfo = props.csaInfo;

      const product = props.meterInfo.product;
      const meterInfo = props.meterInfo;

      const start_date = moment(meterInfo.requestedStartDate).format("YYYY-MM-DD");

      let promo_code = getProperPromoCode(csaInfo.csa_type, meterInfo.promo_code)
      
      if (!promo_code) {
        promo_code = ""
      }

      const payload = {
        formData: {
          firstName: userInfo.first_name,
          lastName: userInfo.last_name,
          language: userInfo.language,
          billingAddress: userInfo.billing_address,
          birthdate: moment(userInfo.birthday).utc().format("YYYY-MM-DD"),
          requestedStartDate: meterInfo.firstAvailableDate ? undefined : start_date,
          enrollMode: meterInfo.enrollMode,
          firstAvailableDate: meterInfo.firstAvailableDate,
          address: meterInfo.address,
          meterUID: meterInfo.meterUID,
          promoCode: promo_code,
          product: !product ? {} : {
            ...product,
            promo_code,
            term_months: product.term_months.toString(),
            max_deposit: product.max_deposit.toString(),
            start_date,
          },
          memberAgreement: true,
          sendEnrollmentCompleteNotification: false,
          sendDepositNotification: false,
          enrollmentCompletionDate: new Date().toISOString(),
          csaID: csaInfo.id,
          company_name: csaInfo.company_name,
        },
        enrollmentType: "add_meter",
        createdBy: localStorage.getItem("email"),
        email: userInfo.email,
        phone: userInfo.phone,
        tdsp_name: meterInfo.tdsp_name,
      };

      const res = await fetch(constants.ENROLL_RENEWAL_OFFER, payload);
      if (res && res.enrollmentID) {
        const deposit = await fetch(constants.ENROLL_GET_DEPOSIT_URL, { enrollmentID: res.enrollmentID })
        if (deposit) {
          const success = await fetch(constants.ENROLLMENT_RESUBMIT_URL, {
            enrollmentID: res.enrollmentID,
            is_waived_deposit: true,
            is_verified: true,
            is_identity_verified: true,
            is_completed: true,
            note: "CSA Meter"
          })

          if (success) {
            dispatch(status.setSuccessMessage("Enrollment submitted"));

            if (csaInfo.id) {
              dispatch(receiveNewEnrollmentId(res.enrollmentID));

            }
          }
        }
      }
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  };
}

function receiveNewEnrollmentId(enrollmentID) {
  return {
    type: constants.CSA_RECEIVE_NEW_ENROLLMENT_ID,
    payload: { enrollmentID },
  }
}

async function getUsers(params) {
  const user_info = await fetch(constants.USERS_MANAGEMENT_GET_USERS, params);
  if (user_info.users && user_info.users.length > 0) {
    return user_info.users;
  }
  return null;
}

// List all CSAs. If given an email only return CSA for that user.
export function listCSAs({ email, company, page, limit }) {
  return async (dispatch) => {
    dispatch(status.startProcessing());
    try {
      const filter = { page, limit };
      if (email) {
        const users = await getUsers({ search: email });
        if (users) {
          filter['user_id'] = { values: users.map(x => x.id) };
        }
      }
      if (company) {
        filter['company'] = company
      }
      const response = await fetch(constants.CSA_LIST_CSA_URL, filter);
      dispatch(receiveCSAs(response.csas || [], Number(response.total) || 0));
    } catch (error) {
      dispatch(status.setErrorMessage(error.message));
    } finally {
      dispatch(status.finishProcessing());
    }
  };
}

function receiveCSAs(csas, total) {
  return {
    type: constants.CSA_LIST_CSA,
    payload: { csas, total },
  };
}
